import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import proactiveDigestsService, { type ProactiveDigestRun } from '../services/proactiveDigestsService';

const DigestRunPage: React.FC = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState<ProactiveDigestRun | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await proactiveDigestsService.getRun(id);
        setRun(res.data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load digest');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id]);

  const actions: Array<{ title?: string; description?: string; link?: string }> = Array.isArray((run as any)?.metrics?.actions)
    ? ((run as any).metrics.actions as any[])
    : [];
  const assumptions: string[] = Array.isArray((run as any)?.metrics?.assumptions) ? ((run as any).metrics.assumptions as any[]).map(String) : [];
  const sourcesObj = (run as any)?.metrics?.sources;
  const sourcesText: string[] = [];
  if (sourcesObj?.crm) sourcesText.push('CRM metrics: deals (Closed Won) MTD');
  if (sourcesObj?.cold_email) sourcesText.push('Cold email metrics: email_tracking');
  if (sourcesObj?.telemarketing) sourcesText.push('Telemarketing metrics: telemarketing_calls');

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {loading ? (
          <div className="text-sm text-gray-600">Loading…</div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        ) : !run ? (
          <div className="text-sm text-gray-600">Digest not found.</div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {run.digest_type === 'morning' ? 'Morning briefing' : run.digest_type === 'lunch' ? 'Lunchtime briefing' : 'Alert'}
                  </h1>
                  <div className="mt-1 text-sm text-gray-600">{run.for_date}</div>
                </div>
                <div className="text-xs text-gray-500">{new Date(run.created_at).toLocaleString()}</div>
              </div>

              {run.is_test ? (
                <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                  This is a test digest run. No notifications or emails were sent.
                </div>
              ) : null}

              {actions.length ? (
                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-900">Top actions today</div>
                  <div className="mt-2 space-y-2">
                    {actions.slice(0, 6).map((a, idx) => (
                      <div key={idx} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <div className="text-sm font-medium text-gray-900">{String(a.title || 'Action')}</div>
                        {a.description ? <div className="mt-1 text-sm text-gray-600">{String(a.description)}</div> : null}
                        {a.link ? (
                          <a className="mt-2 inline-block text-sm text-blue-600 hover:underline" href={String(a.link)}>
                            {String(a.link)}
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {sourcesText.length || assumptions.length ? (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm font-medium text-gray-900">Sources</div>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                      {(sourcesText.length ? sourcesText : ['No sources metadata available.']).join('\n')}
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm font-medium text-gray-900">Assumptions</div>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                      {(assumptions.length ? assumptions : ['No assumptions recorded.']).join('\n')}
                    </div>
                  </div>
                </div>
              ) : null}

              {run.summary_text ? (
                <pre className="mt-4 whitespace-pre-wrap rounded-md bg-gray-50 p-4 text-sm text-gray-800">
                  {run.summary_text}
                </pre>
              ) : null}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <details>
                <summary className="cursor-pointer text-sm font-medium text-gray-900">Raw metrics</summary>
                <pre className="mt-3 whitespace-pre-wrap rounded-md bg-gray-50 p-4 text-xs text-gray-800">
                  {JSON.stringify(run.metrics, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigestRunPage;
